import httpClient from "../../clients/httpClient";
import {IAdminDashboardStats, IGoogleAccountInfo, IOrganizationPayload, IOrganizationResponse, IUpdateProjectPayload} from "../../interfaces/organization.interface";
import { IIntegrationListResponse, IOnboardingVerifyResponse } from "../../interfaces/integration.interface";
import { GetAdminOrganizationsPayload } from "../../state/types/admin-organization";
import {
    IMessageCenterDetail,
    IMessageCenterFilterOptions,
    IMessageCenterItem,
    IMessageCenterNote,
    IMessageCenterQuery,
    IMessageCenterResource,
} from "../../interfaces/message-center.interface";

export const getOrganizations = (query: GetAdminOrganizationsPayload) => {
    return httpClient.get<IOrganizationResponse>(`/api/v1/admin/organizations`, {
        params: {
            search: query.search,
            per_page: query.perPage,
            page: query.page,
            exclude_completed: query.exclude_completed,
        }
    });
};

export const createOrganization = (payload: IOrganizationPayload) => {
    return httpClient.post("/api/v1/admin/organizations", payload);
};

export const editOrganization = (id: number, payload: IOrganizationPayload) => {
    return httpClient.patch(`/api/v1/admin/organizations/${id}`, payload);
};

export const deleteOrganization = (id: number) => {
    return httpClient.delete(`/api/v1/admin/organizations/${id}`);
};

export const getEmployees = (id: number) => {
  return httpClient.get(`/api/v1/admin/organizations/${id}`);
};

export const showOrganization = (id: string | number) => {
    return httpClient.get(`/api/v1/admin/organizations/${id}`, {
        params: { include: 'promotionSettings' }
    });
};

export const uploadLogo = (id: number, payload: any) => {
    return httpClient.post(`/api/v1/admin/organizations/${id}/logo`, payload);
}

export const getGoogleAccountInfo = () => {
    return httpClient.get<IGoogleAccountInfo>(`/api/v1/google-account-info`);
}

export const getOrganizationProject = (id: number) =>
    httpClient.get(`/api/v1/admin/organizations/${id}/project`);

export const updateOrganizationProject = (id: number, payload: IUpdateProjectPayload) =>
    httpClient.patch(`/api/v1/admin/organizations/${id}/project`, payload);

export const reopenDesignApproval = (orgId: number, note: string) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/project/design-review/reopen`, { note });

export const getMilestonePhases = (milestone: string) =>
    httpClient.get(`/api/v1/admin/organizations/milestone-phases`, {
        params: { milestone },
    });

export const getOrganizationProjectHistory = (id: number) =>
    httpClient.get(`/api/v1/admin/organizations/${id}/project/history`);

export const getAdminDashboardStats = (projectManagerId?: number | null) =>
    httpClient.get<IAdminDashboardStats>(`/api/v1/admin/organizations/dashboard-stats`, {
        params: projectManagerId != null ? { project_manager_id: projectManagerId } : {},
    });

export const getAdminMilestoneSchedule = (orgId: number) =>
    httpClient.get(`/api/v1/admin/organizations/${orgId}/project/milestone-schedule`);

export const getAdminMilestoneScheduleHistory = (orgId: number) =>
    httpClient.get(`/api/v1/admin/organizations/${orgId}/project/milestone-schedule/history`);

export const setAdminMilestoneStartDate = (
    orgId: number,
    startDate: string,
    note: string,
    reported: boolean
) =>
    httpClient.post(
        `/api/v1/admin/organizations/${orgId}/project/milestone-schedule/set-start-date`,
        { start_date: startDate, note, reported }
    );

export const updateAdminMilestoneCheckpoint = (
    orgId: number,
    checkpointId: number,
    date: string,
    markComplete: boolean,
    note: string,
    reported: boolean
) =>
    httpClient.patch(
        `/api/v1/admin/organizations/${orgId}/project/milestone-schedule/checkpoints/${checkpointId}`,
        { date, mark_complete: markComplete, note, reported }
    );

// Checklist (admin)
export const getAdminChecklist = (orgId: number) =>
    httpClient.get(`/api/v1/admin/organizations/${orgId}/checklist`);

export const completeAdminChecklistItem = (orgId: number, itemId: number) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/checklist/items/${itemId}/complete`);

export const reopenAdminChecklistItem = (orgId: number, itemId: number) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/checklist/items/${itemId}/reopen`);

export const sendBackAdminChecklistItem = (orgId: number, itemId: number, message: string) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/checklist/items/${itemId}/send-back`, { message });

export const updateAdminDeveloperAccount = (
    orgId: number,
    itemId: number,
    payload: { org_name?: string | null; invite_sent?: boolean }
) =>
    httpClient.patch(
        `/api/v1/admin/organizations/${orgId}/checklist/items/${itemId}/developer-account`,
        payload
    );

// Integrations (admin)
export const getAdminIntegrationTemplates = () =>
    httpClient.get('/api/v1/admin/organizations/integrations/templates');

export const getAdminIntegrations = (orgId: number) =>
    httpClient.get(`/api/v1/admin/organizations/${orgId}/integrations`);

export const getAdminIntegrationsList = (
    orgId: number,
    params?: { page?: number; per_page?: number }
) =>
    httpClient.get<IIntegrationListResponse>(
        `/api/v1/admin/organizations/${orgId}/integrations/list`,
        { params }
    );

// Create an onboarding integration (draft). The type is expressed as the six
// is_* flags constrained to what the chosen template supports — the old
// `integration_type` string is gone.
export const addAdminIntegration = (
    orgId: number,
    payload: {
        integration_template_id: number;
        name: string;
        is_rewards: boolean;
        is_ecommerce: boolean;
        is_delivery: boolean;
        is_gift_card: boolean;
        is_payment_gateway: boolean;
        is_programmatic_ads: boolean;
    }
) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/integrations`, payload);

export const updateAdminIntegrationCredentials = (
    orgId: number,
    integrationId: number,
    data: Record<string, string | number | null>
) =>
    httpClient.patch(
        `/api/v1/admin/organizations/${orgId}/integrations/${integrationId}`,
        { data }
    );

// Runs the real credentials-verification preflight server-side and, on a
// passing result, activates the draft (onboarding_status=verified, is_active).
// Returns { data: { passed, checks[], integration } } — integration is the
// activated onboarding resource on pass, null on fail. Unsupported templates
// pass without a vendor call (the backend acknowledges them); the frontend
// reacts uniformly.
export const verifyAdminIntegration = (
    orgId: number,
    integrationId: number
): Promise<IOnboardingVerifyResponse> =>
    httpClient.post(
        `/api/v1/admin/organizations/${orgId}/integrations/${integrationId}/verify`
    ) as unknown as Promise<IOnboardingVerifyResponse>;

export const sendBackAdminIntegration = (
    orgId: number,
    integrationId: number,
    message: string
) =>
    httpClient.post(
        `/api/v1/admin/organizations/${orgId}/integrations/${integrationId}/send-back`,
        { message }
    );

export const deleteAdminIntegration = (orgId: number, integrationId: number) =>
    httpClient.delete(
        `/api/v1/admin/organizations/${orgId}/integrations/${integrationId}`
    );

// App Store Listing (admin)
export const getAdminAppStoreListing = (orgId: number) =>
    httpClient.get(`/api/v1/admin/organizations/${orgId}/app-store-listing`);

export const updateAdminAppStoreListing = (orgId: number, payload: FormData) =>
    httpClient.post(
        `/api/v1/admin/organizations/${orgId}/app-store-listing`,
        payload
    );

export const approveAdminAppStoreListing = (orgId: number) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/app-store-listing/approve`);

export const reopenAdminAppStoreListing = (orgId: number) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/app-store-listing/reopen`);

export const sendBackAdminAppStoreListing = (orgId: number, message: string) =>
    httpClient.post(
        `/api/v1/admin/organizations/${orgId}/app-store-listing/send-back`,
        { message }
    );

// Feedback (admin)
export const updateOrganizationFeedback = (orgId: number, feedbackId: number, payload: { status?: string; type?: string; platform?: string | null; monday_task_url?: string }) =>
    httpClient.patch(`/api/v1/admin/organizations/${orgId}/feedback/${feedbackId}`, payload);

export const addOrganizationFeedbackReply = (orgId: number, feedbackId: number, payload: FormData) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/feedback/${feedbackId}/replies`, payload);

export const markOrganizationFeedbackRead = (orgId: number, feedbackId: number) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/feedback/${feedbackId}/read`);

export const createOrganizationFeedback = (orgId: number, payload: FormData) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/feedback`, payload);

export const addOrganizationFeedbackNote = (orgId: number, feedbackId: number, body: string) =>
    httpClient.post<IMessageCenterNote>(`/api/v1/admin/organizations/${orgId}/feedback/${feedbackId}/notes`, { body });

// Message Center (admin)
export const getMessageCenterItems = (params: IMessageCenterQuery) =>
    httpClient.get<IMessageCenterResource<IMessageCenterItem>[]>(`/api/v1/admin/message-center`, { params });

export const getMessageCenterFilters = () =>
    httpClient.get<IMessageCenterFilterOptions>(`/api/v1/admin/message-center/filters`);

export const getMessageCenterItem = (feedbackId: number) =>
    httpClient.get<IMessageCenterResource<IMessageCenterDetail>>(`/api/v1/admin/message-center/${feedbackId}`);

export const getOrganizationInvitations = (orgId: number) => {
    return httpClient.get(`/api/v1/admin/organizations/${orgId}/invitations`, {
        params: { per_page: 50 }
    });
};

export const resendOrganizationInvitation = (invitationId: number) => {
    return httpClient.patch(`/api/v1/admin/invitations/${invitationId}/resend`);
};

// App Testing (admin)
export const setOrganizationTestflightUrl = (orgId: number, url: string | null) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/app-testing/testflight-url`, { url });

export const setOrganizationAndroidTestingUrl = (orgId: number, url: string | null) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/app-testing/android-testing-url`, { url });

export const getOrganizationAndroidTesters = (orgId: number) =>
    httpClient.get(`/api/v1/admin/organizations/${orgId}/app-testing/android-testers`);

export const markAndroidTesterAdded = (orgId: number, testerId: number) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/app-testing/android-testers/${testerId}/mark-added`);

export const markAllAndroidTestersAdded = (orgId: number) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/app-testing/android-testers/mark-all-added`);

export const removeAndroidTester = (orgId: number, testerId: number) =>
    httpClient.delete(`/api/v1/admin/organizations/${orgId}/app-testing/android-testers/${testerId}`);

// PM Intel (admin)
export const updateOrgPmIntel = (orgId: number, payload: {
    risk_level?: string | null;
    last_communication_date?: string | null;
    last_response_date?: string | null;
    action_required?: boolean;
    action_notes?: string | null;
}) => httpClient.patch(`/api/v1/admin/organizations/${orgId}/pm-intel`, payload);

export const getOrgPmNotes = (orgId: number) =>
    httpClient.get(`/api/v1/admin/organizations/${orgId}/pm-intel/notes`);

export const addOrgPmNote = (orgId: number, body: string) =>
    httpClient.post(`/api/v1/admin/organizations/${orgId}/pm-intel/notes`, { body });